import { Navigate, Outlet, useLocation } from "react-router";
import { Loader } from "lucide-react";

import { authClient } from "@/lib/auth-client";
import { useMySubscription } from "@/hooks/queries/useMySubscription";
import {
  SURETY_PLAN_NAME,
  WORK_ADVISORY_PLAN_NAME,
} from "@/lib/domain-constants";
import { getLandingRoute } from "@/lib/landing";

// @ts-ignore
import licitigo_logo from "../../assets/licitigo_logo.svg";

const WORK_ADVISORY_ROUTE = "/work-advisory";

/**
 * Rutas que una constructora sí puede abrir además de la suya: la ficha de una
 * obra —a la que enlaza su propio listado— y la configuración de la cuenta, de
 * donde cuelga el cierre de sesión.
 */
const WORK_ADVISORY_ALLOWED = [
  WORK_ADVISORY_ROUTE,
  "/contracting/",
  "/settings",
];

const SURETY_ROUTE = "/surety";

/** Lo que abre el plan FIANZAS: su portal y las fichas a las que enlaza. */
const SURETY_ALLOWED = [
  SURETY_ROUTE,
  "/contracting/",
  "/companies/",
  "/settings",
];
// Dentro de lo permitido, lo que no le toca: los documentos son para el cruce de requisitos.
const SURETY_EXCLUDED = ["/settings/documents"];

/**
 * El plan decide a qué vistas se entra, en los dos sentidos: con ASESORIA DE
 * OBRAS la cuenta vive en su informe y nada más; sin él, esa vista no existe.
 *
 * Duplica a propósito el gate que el backend ya aplica (`requireWorkAdvisoryPlan`):
 * el backend es el que manda, esto solo evita que el usuario aterrice en una
 * pantalla que va a fallar.
 */
export default function PlanGuard() {
  const { data: subscription, isPending } = useMySubscription();
  const { data: session } = authClient.useSession();
  const { pathname } = useLocation();
  // El equipo de Licitigo entra a mirar lo mismo que el cliente. Espeja el
  // bypass del backend (`requireWorkAdvisoryPlan`), que es el que manda.
  const isAdmin =
    (session?.user as { role?: string | null } | undefined)?.role === "admin";

  // Solo la vista de la asesoría necesita la respuesta para decidir. Al resto se
  // le deja pasar mientras carga: bloquear toda la app a la espera de un plan que
  // casi nadie tiene sería pagarle a todos el costo de uno.
  if (isPending) {
    return pathname.startsWith(WORK_ADVISORY_ROUTE) ||
      pathname.startsWith(SURETY_ROUTE) ? (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 py-24">
        <img src={licitigo_logo} alt="Licitigo" className="h-14 w-auto" />
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    ) : (
      <Outlet />
    );
  }

  const hasWorkAdvisoryPlan = subscription?.planKey === WORK_ADVISORY_PLAN_NAME;

  if (
    hasWorkAdvisoryPlan &&
    !WORK_ADVISORY_ALLOWED.some((route) => pathname.startsWith(route))
  ) {
    return <Navigate to={WORK_ADVISORY_ROUTE} replace />;
  }

  // Sin el plan la vista no existe, salvo para un admin. Ojo con el orden: el
  // bloque de arriba encierra a quien TIENE el plan en su vista, y a un admin no
  // hay que encerrarlo — por eso el bypass va solo acá.
  if (
    !hasWorkAdvisoryPlan &&
    !isAdmin &&
    pathname.startsWith(WORK_ADVISORY_ROUTE)
  ) {
    return <Navigate to={getLandingRoute(subscription)} replace />;
  }

  // Fianzas, con las mismas dos reglas: su plan vive en el portal, y sin el plan
  // el portal solo lo abre un admin (la demo).
  const hasSuretyPlan = subscription?.planKey === SURETY_PLAN_NAME;
  if (
    hasSuretyPlan &&
    (!SURETY_ALLOWED.some((route) => pathname.startsWith(route)) ||
      SURETY_EXCLUDED.some((route) => pathname.startsWith(route)))
  ) {
    return <Navigate to={SURETY_ROUTE} replace />;
  }
  if (!hasSuretyPlan && !isAdmin && pathname.startsWith(SURETY_ROUTE)) {
    return <Navigate to={getLandingRoute(subscription)} replace />;
  }

  return <Outlet />;
}
